import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Button,
    CircularProgress,
    Alert,
    AlertTitle,
    Box,
    Typography,
    Tooltip,
    TableRow
} from '@mui/material';
import {
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { get } from '../hooks/apiFetch';
import { formatDateTime } from '../../helpers/ai/shared/TimeFormatter';

const getStatusLabel = (status) => {
    const statusMap = {
        JobAdded: 'Добавлена',
        "Finished True": 'Завершена+',
        "Finished False": 'Завершена-',
        Executing: 'Выполняется',
        Cancelled: 'Отменено',
        Error: 'Ошибка',
    };
    return statusMap[status] || status;
};

const sortRecords = (records, sortConfig) => {
    if (!sortConfig.key || !records || records.length === 0) return records;

    return [...records].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (sortConfig.key === 'id') {
            aValue = Number(aValue);
            bValue = Number(bValue);
        }
        else if (sortConfig.key === 'date' || sortConfig.key === 'startTime') {
            aValue = new Date(aValue).getTime();
            bValue = new Date(bValue).getTime();
            if (isNaN(aValue)) aValue = -Infinity;
            if (isNaN(bValue)) bValue = -Infinity;
        }
        else {
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();
            if (aValue === '' && bValue !== '') return 1;
            if (aValue !== '' && bValue === '') return -1;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
};

export const InputDataTab = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [lastRecordId, setLastRecordId] = useState(null);
    const [hasMoreRecords, setHasMoreRecords] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    const loadingRef = useRef(false);

    useEffect(() => {
        if (records.length === 0) return;
        setRecords(prev => sortRecords(prev, sortConfig));
    }, [sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prevConfig) => {
            let direction = 'asc';
            if (prevConfig.key === key && prevConfig.direction === 'asc') {
                direction = 'desc';
            }
            const newConfig = { key, direction };
            // Пересортировать текущие записи
            const sorted = sortRecords(records, newConfig);
            setRecords(sorted);
            return newConfig;
        });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <UnfoldMoreIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />;
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
            : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />;
    };

    const loadRecords = useCallback(async () => {
        if (loading || !hasMoreRecords) return;

        setLoading(true);
        setError(null);

        try {
            const url = `/api/admin/datauidb/GetChunk?lastId=${encodeURIComponent(lastRecordId || '')}&take=${encodeURIComponent(50)}`;
            loadingRef.current = true;
            const newData = await get(url);
            const merged = [...records, ...newData];
            const sorted = sortRecords(merged, sortConfig);

            setRecords(sorted);
            if (newData.length > 0) {
                setLastRecordId(newData[newData.length - 1].id);
            }
            setHasMoreRecords(newData.length > 40);
        } catch (err) {
            setError(err.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [lastRecordId, hasMoreRecords, records, sortConfig]);

    useEffect(() => {
        loadRecords();
    }, []);

    if (loading && records.length === 0) {
        return (
            <Box display="flex" justifyContent="center" m={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box m={3}>
                <Alert variant="outlined" severity="error">
                    <AlertTitle>Ошибка</AlertTitle>
                    {error}
                </Alert>
                <Box mt={2}>
                    <Button className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button" onClick={loadRecords}>
                        Повторить
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box m={3}>
            <div className="table-responsive">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                                ID {getSortIcon('id')}
                            </th>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('utility')} style={{ cursor: 'pointer' }}>
                                Утилита {getSortIcon('utility')}
                            </th>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                                Дата {getSortIcon('date')}
                            </th>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('creatorName')} style={{ cursor: 'pointer' }}>
                                Автор {getSortIcon('creatorName')}
                            </th>
                            <th className="text-left align-middle">Входные данные</th>
                            <th className="text-left align-middle">Выходные данные</th>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('startTime')} style={{ cursor: 'pointer' }}>
                                Время старта {getSortIcon('startTime')}
                            </th>
                            <th className="text-left align-middle sortable" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                Статус {getSortIcon('status')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((record) => (
                            <TableRow key={record.id} hover>
                                <td className="text-left align-middle">{record.id}</td>
                                <td className="text-left align-middle">{record.utility}</td>
                                <td className="text-left align-middle">{formatDateTime(record.date) || '-'}</td>
                                <td className="text-left align-middle">{record.creatorLogin || '-'}</td>
                                <td className="text-left align-middle">
                                    <Tooltip title={record.inputData} arrow>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                            {record.inputData}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td className="text-left align-middle">
                                    <Tooltip title={record.outputData} arrow>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                            {record.outputData}
                                        </Typography>
                                    </Tooltip>
                                </td>
                                <td className="text-left align-middle">{formatDateTime(record.startTime) || '-'}</td>
                                <td className="text-left align-middle">{getStatusLabel(record.status)}</td>
                            </TableRow>
                        ))}
                    </tbody>
                </table>
            </div>

            {loading && (
                <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress size={30} />
                </Box>
            )}

            {hasMoreRecords && !loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <Button className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button" onClick={loadRecords}>
                        Загрузить еще
                    </Button>
                </Box>
            )}
        </Box>
    );
};